import { useAtomValue } from 'jotai';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ActivityCleanFilterIcon } from '@/lib/icon';
import { Button, DateInput, Dropdown, Flex, IconSvg, InputSearch } from '@/lib/v2/components';
import { eventOnChangeDateInput } from '@/lib/v2/components/DateInput/DateInput';
import { DropdownItemProps } from '@/lib/v2/components/Dropdown';
import { StateIcon, StrategyIcon } from '@/lib/v2/icons/outline';

import { STRATEGY_MAP_BY_ID } from '@/src/modules/CampaignsModule/components/CampaignsModal/CreateEditCampaigns/constants';

import { atomStrategyList } from '@/modules/CampaignsModule/atoms/campaignAtom';
import { DROPDOWN_STATE } from '@/modules/CampaignsModule/constants';
import {
  useCampaignsMutationContext,
  useCampaignsStateContext,
} from '@/modules/CampaignsModule/contexts/CampaignsContext';
import { clearObjectFalsy, strategyOrderOtherOption } from '@/modules/CampaignsModule/utils';

const FilterBar = () => {
  const { t } = useTranslation();
  const strategyList = useAtomValue(atomStrategyList);

  const { filterBy, clearFilterBy } = useCampaignsStateContext();
  const { setFilterBy, setClearFilterBy, resetPagination } = useCampaignsMutationContext();

  const [strategiesList, setStrategiesList] = useState<DropdownItemProps[]>([]);
  const [querySearch, setQuerySearch] = useState<string>(filterBy?.search ?? '');
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    filterBy?.startDate ?? null,
    filterBy?.endDate ?? null,
  ]);

  useEffect(() => {
    const strategiesListCast = strategyOrderOtherOption(strategyList);
    setStrategiesList(strategiesListCast);
  }, [strategyList]);

  const handleOnClickSearch = useCallback(() => {
    const currentFilterBy = { ...filterBy, search: querySearch };
    const clearedFiltersBy = clearObjectFalsy(currentFilterBy);

    setFilterBy(clearedFiltersBy);
    resetPagination();
  }, [filterBy, querySearch, resetPagination, setFilterBy]);

  const handleOnChangeSearch = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setQuerySearch(event.target.value);
  }, []);

  const handleOnClearSearch = useCallback(() => {
    setQuerySearch('');
    const currentFilterBy = { ...filterBy, search: '' };
    const clearedFiltersBy = clearObjectFalsy(currentFilterBy);
    setFilterBy(clearedFiltersBy);
  }, [filterBy, setFilterBy]);

  const handleOnClickState = useCallback(
    (data: DropdownItemProps[]) => {
      const stateID: number[] = data.map((item) => item.value as number);
      const currentFilterBy = { ...filterBy, state: stateID };
      const clearedFiltersBy = clearObjectFalsy(currentFilterBy);

      setFilterBy(clearedFiltersBy);
      resetPagination();
    },
    [filterBy, resetPagination, setFilterBy]
  );

  const handleOnClickStrategy = useCallback(
    (data: DropdownItemProps[]) => {
      const strategyID: number[] = data.map((item) => item.value as number);
      const currentFilterBy = { ...filterBy, strategy: strategyID };
      const clearedFiltersBy = clearObjectFalsy(currentFilterBy);

      setFilterBy(clearedFiltersBy);
      resetPagination();
    },
    [filterBy, resetPagination, setFilterBy]
  );

  const handleOnChangeDate: eventOnChangeDateInput = useCallback(
    (event) => {
      if (Array.isArray(event)) {
        setDateRange(event as [Date | null, Date | null]);
        if (event[0] && event[1]) {
          setFilterBy({ ...filterBy, startDate: event[0], endDate: event[1] });
          resetPagination();
        }
      }
    },
    [filterBy, resetPagination, setFilterBy]
  );

  const handleReset = useCallback(() => {
    setQuerySearch('');
    setDateRange([null, null]);
    setFilterBy({});
    resetPagination();
  }, [resetPagination, setFilterBy]);

  useEffect(() => {
    if (clearFilterBy) {
      handleReset();
      setClearFilterBy(false);
    }
  }, [clearFilterBy, handleReset, setClearFilterBy]);

  return (
    <div className="z-10 mb-[-72px] flex max-w-fit items-center gap-2 px-2">
      <div className="w-72">
        <InputSearch
          autoComplete="off"
          id="search-campaigns"
          placeHolder={t('CAMPAIGNS_MAIN.FILTERS.searchPlaceholder')}
          value={querySearch}
          onAction={handleOnClickSearch}
          onChange={handleOnChangeSearch}
          onClear={handleOnClearSearch}
        />
      </div>
      <Dropdown
        checkBox
        buttonOnClick={handleOnClickState}
        buttonText={t('CAMPAIGNS_MAIN.FILTERS.button')}
        dropdownItems={DROPDOWN_STATE.map((state) => ({
          ...state,
          label: t(`CAMPAIGNS_MAIN.STATES.${state.label}`),
        }))}
        icon={<IconSvg svgComponent={<StateIcon />} />}
        id="state-campaigns"
        label={t('CAMPAIGNS_MAIN.FILTERS.state')}
      />
      <Dropdown
        checkBox
        checkboxContainerWithScroll
        withSearch
        buttonOnClick={handleOnClickStrategy}
        buttonText={t('CAMPAIGNS_MAIN.FILTERS.button')}
        dropdownItems={strategiesList
          .map((strategy) => ({
            ...strategy,
            label: t(
              `CAMPAIGNS_MAIN.DROPDOWN_STRATEGIES.${STRATEGY_MAP_BY_ID[Number(strategy.id)]}`,
              strategy.label
            ),
            value: t(
              `CAMPAIGNS_MAIN.DROPDOWN_STRATEGIES.${strategy.value}`,
              String(strategy.value)
            ),
          }))
          .sort((a, b) => a.label.localeCompare(b.label))}
        icon={<IconSvg svgComponent={<StrategyIcon />} />}
        id="strategy-campaigns"
        label={t('CAMPAIGNS_MAIN.FILTERS.strategy')}
        searchPlaceholder={t('CAMPAIGNS_MAIN.FILTERS.searchDropdown')}
      />
      <DateInput
        isRequired
        selectsRange
        endDate={dateRange[1]}
        id="date-campaigns"
        maxDate={new Date()}
        name="date"
        placeholder={t('CAMPAIGNS_MAIN.FILTERS.date')}
        startDate={dateRange[0]}
        onChange={handleOnChangeDate}
      />
      <Button
        standard
        disabled={Object.keys(filterBy).length === 0}
        iconLeft={<IconSvg fillColor="primary" svgComponent={<ActivityCleanFilterIcon />} />}
        id="clear-filters-button"
        tooltip={t('CAMPAIGNS_MAIN.FILTERS.buttonClear')}
        onClick={handleReset}
      ></Button>
    </div>
  );
};

export default FilterBar;
